import { useEffect, useRef, useState } from "react";
import type { OnboardingSubmissionView } from "../hooks/useAdminClaws";
import styles from "./OnboardingSubmissionDialog.module.css";

type Props = {
  open: boolean;
  tenantId: string | null;
  tenantName: string | null;
  fetch: (tenantId: string) => Promise<OnboardingSubmissionView | null>;
  onClose: () => void;
};

export const OnboardingSubmissionDialog = ({ open, tenantId, tenantName, fetch, onClose }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [data, setData] = useState<OnboardingSubmissionView | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    if (!open || !tenantId) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(tenantId)
      .then((view) => {
        if (cancelled) return;
        if (!view) {
          setError("No onboarding submission for this instance.");
          setData(null);
        } else {
          setData(view);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, fetch]);

  if (!open) return null;

  return (
    <dialog ref={dialogRef} className={styles.dialog} onClose={onClose}>
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>Onboarding submission</span>
          <h2 className={styles.title}>{tenantName ?? "Instance"}</h2>
        </div>
        {loading && <p className={styles.muted}>Loading…</p>}
        {error && <p className={styles.error}>{error}</p>}
        {data && <pre className={styles.payload}>{JSON.stringify(data, null, 2)}</pre>}
        <div className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </dialog>
  );
};
